import React from "react";
import styled, { css } from "styled-components";
import { useCombobox } from "downshift";

import { useSearchApi } from "src/core/shared/api/search.api";
import {
  FilterParams,
  ITerminology
} from "src/library/utils";
import { Text } from "src/library/components/Text";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { IDrop } from "src/core/drops/api/drops.api";

import { DropSearchComponent } from "./DropSearchComponent";

// force consumer to hold drop state
export interface SelectedDropProps {
  drop: IDrop | null;
  handleUpdate: (data: IDrop | null) => void;
  terminology?: ITerminology;
  disableRemoveDrop?: boolean;
  disabled?: boolean;
  placeHolder?: string;
}

/**
 * A field that displays selected drop, however is amendable with a search drops feature.
 */
const SelectedDrop: React.FC<SelectedDropProps> = ({
  drop, handleUpdate, terminology, placeHolder, disableRemoveDrop, disabled = false
}) => {
  const [ listSearchResponse, getSearch ] = useSearchApi("SEARCH_DROPS");
  const [ showSearch, setShowSearch ] = React.useState(false);
  const [ isLoading, setIsLoading ] = React.useState(false);

  React.useEffect(() => {
    getSearch({
      pagination: {
        offset: 0,
        page: 0,
        limit: 30,
        sort: [ [ "name", "ASC" ] ]
      },
      filters: {}
    });
  }, [ getSearch ]);

  // handle selecting a drop
  const dropDownShiftProps = useCombobox<IDrop>({
    items: listSearchResponse.data ? listSearchResponse.data.result : [],
    itemToString: () => "",
    onSelectedItemChange: changes => {
      if (changes.selectedItem) {
        handleUpdate(changes.selectedItem);
        setShowSearch(false);

        return;
      }
    }
  });

  // update local loading state
  React.useEffect(() => {
    if (drop || drop === null) {
      setIsLoading(false);
    }
  }, [ drop ]);

  if (showSearch) {
    return (
      <DropSearchComponent
        error={listSearchResponse.error}
        terminology={terminology}
        onSearch={(data: string) => {
          const filterData: FilterParams<IDrop> = { $or: [ { name: { $like: `%${data}%` } }, { branchNumber: { $like: `%${data}%` } } ] };

          getSearch({
            pagination: {
              offset: 0,
              page: 0,
              limit: 30,
              sort: [ [ "name", "ASC" ] ]
            },
            filters: filterData
          });
        }}
        loading={listSearchResponse.pending}
        searchResults={listSearchResponse?.data?.result || []}
        downshiftProps={dropDownShiftProps}
        placeholder={`Search ${terminology?.drop.plural.text || "drops"}...`}
        onClose={() => setShowSearch(false)}
        autoFocus={showSearch}
      />
    );
  }

  return (
    <SelectedDropWrapper
      hasDrop={!!drop}
      disabled={disabled}
    >
      {!isLoading && (
        <div
          className="selected-drop"
          onClick={() => {
            if (!disabled) {
              setShowSearch(!showSearch);
            }
          }}
        >
          {!drop && (
            <Icon
              className="drop-icon"
              icon={Icons.BRANCH}
              $fillColor="midGrey"
            />
          )}

          <Text>
            {drop ? `(${drop.branchNumber}) ${drop.name}` : placeHolder || " - "}
          </Text>
        </div>
      )}

      {isLoading && (
        <div className="loading-container">
          <LoadingSpinner
            type="flat"
            position="inline"
          />
        </div>
      )}

      {drop && !disabled && !disableRemoveDrop && (
        <div className="remove-drop">
          <Icon
            icon={Icons.CLOSE}
            $fillColor="primaryDark"
            onClick={() => {
              handleUpdate(null);
            }}
          />
        </div>
      )}
    </SelectedDropWrapper>
  );
};

const SelectedDropWrapper = styled.div<{hasDrop: boolean, disabled: boolean}>`
  ${({
    theme, hasDrop, disabled
  }) => css`
    max-width: 400px;
    display: flex;

    .loading-container {
      min-width: 160px;
      display:flex;
      align-items: center;
    }

    .selected-drop {
      color: black;
      background-color: ${theme.colors.white};
      border: 1px solid ${theme.colors.lightGrey};
      height: 40px;
      border-radius: ${theme.borderRadius};
      display: flex;
      align-items: center;
      padding: ${theme.spacing.padding.sm} ${theme.spacing.padding.md};
      justify-content: ${hasDrop ? "space-between" : "space-start"};
      font-weight: bold;
      width: 100%;
  
      &:hover {
        border-color: ${theme.colors.midGrey};
        cursor: ${disabled ? "not-allowed" : "pointer"};
      }

    }
    
    .drop-icon {
      margin-right: ${theme.spacing.margins.md};
    }

    .remove-drop {
      margin-left: ${theme.spacing.margins.sm};
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
    }
  `}
`;

export default SelectedDrop;
